// chartConfig.ts
import type { ChartInfo } from '../types/commontypes';
import type { ChartId } from '../types/commontypes';

const height = 720;
export const subChartHeight = 100;
export const margin = { left: 0, right: 70, top: 9, bottom: 20 };
const gridHeight = height - margin.top - margin.bottom;
export const chartHeight = gridHeight - subChartHeight * 5;

// Initial heights for all charts
export const initialChartInfos: ChartInfo[] = [
  { chartId: `chartId_ohlcv`, height: chartHeight, label: 'OHLCV', isCandleChart: true }, // Main candlestick chart
  { chartId: `chartId_volume`, height: subChartHeight, label: 'Volume' }, // volume bar chart
  { chartId: `chartId_full_sto`, height: subChartHeight, label: 'Full STO' },
  { chartId: `chartId_rsi`, height: subChartHeight, label: 'RSI' },
  { chartId: `chartId_elder_ray`, height: subChartHeight, label: 'Elder Ray' },
  { chartId: `chartId_volume_2`, height: subChartHeight, label: 'Volume 2' },
];

// Actual visual order from top to bottom (chartId_ohlcv is at top with origin [0,0])
export const chartOrder: ChartId[] = initialChartInfos.map(c => c.chartId);

// Bottom-to-top order (create a reversed copy without mutating chartOrder)
export const chartOrderBottomToTop: ChartId[] = [...chartOrder].reverse();

// Calculate total height of all charts except margin top, margin bottom
export const getTotalChartHeight = (chartInfos: ChartInfo[]) => chartInfos.reduce((sum, ch) => sum + ch.height, 0);

// Calculate the ratio of each chart height to the total
export const getChartHeightRatios = (chartInfos: ChartInfo[]) => {
  const total = getTotalChartHeight(chartInfos);
  return chartInfos.map(ch => ({
    chartId: ch.chartId,
    ratio: ch.height / total,
  }));
};

// Scale chart heights proportionally based on new total height
export const scaleChartHeights = (
  chartInfos: ChartInfo[],
  newTotalHeight: number,
  margin: { top: number; bottom: number },
): ChartInfo[] => {
  const availableHeight = newTotalHeight - margin.top - margin.bottom;
  const ratios = getChartHeightRatios(chartInfos);

  return ratios.map(({ ratio }, idx) => ({
    ...chartInfos[idx],
    height: availableHeight * ratio,
  }));
};

// Function to generate chartHeightMap from chartInfos
export const getChartHeightMap = (chartInfos: ChartInfo[]) =>
  chartInfos.reduce<Record<ChartId, number>>((acc, ch) => {
    acc[ch.chartId] = ch.height;
    return acc;
  }, {});

export const getDynamicOrigin =
  (chartId: ChartId, chartHeightMap: Record<ChartId, number>) =>
  (_: unknown, h: number): [number, number] => {
    // Find all charts rendered *below* the current one
    const index = chartOrderBottomToTop.indexOf(chartId);

    if (index === -1) {
      return [0, 0];
    }

    // Sum heights of all charts below this one
    const belowHeight = chartOrderBottomToTop.slice(0, index).reduce((sum, id) => sum + (chartHeightMap[id] || 0), 0);

    // Origin is total height minus height of this chart and all below
    return [0, h - belowHeight - chartHeightMap[chartId]];
  };

export const timeframeMap: Record<string, number> = {
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
};
